/* eslint-disable @nx/enforce-module-boundaries */
import { ThreeDot } from '@mybridge/icons';
import { ShareIcon } from '@mybridge/icons/Share';
import {
  Avatar,
  HStack,
  IconButton,
  Link,
  Spinner,
  Stack,
  Text,
  Tooltip,
  Box
} from '@mybridge/ui';
import { useDimensions } from '@mybridge/ui/hooks';
import { SelectableTable } from '@mybridge/ui/table';
import moment from 'moment';
import { forwardRef, useContext, useMemo, useRef, useState } from 'react';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { DiskContext } from '../../context';
import { DiskNoFilesIllustration } from '../../no-files-illustration';
import { SharedDiskUserItemMenu } from '../listing-item-menu';
import { isMobile } from 'react-device-detect';
import {copyTextToClipboard} from 'v4/components/utils';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const SharedDiskUsersListingTable = forwardRef(
  ({ menu, ...props }, ref) => {
    const {
      files,
      setSelectedDir,
      selectedFile,
      setSelectedFile,
      filesLoading,
      checkedFiles,
      onFileSelect,
      setSelectedFiles,
      generateShareLink,
      setSelectedMainDir,
      sharedFileLink,
      refetchSharedFileLink,
      shareDisc,
      selectSharedDiskUser,
      toast,
      userProfileInfo
    } = useContext(DiskContext);
    const [hovered, setHovered] = useState();
    const [copyingLink, setCopyingLink] = useState(false);
    const { t } = useTranslator();
    
    // useEffect(() => {

      
    const cols = useMemo(
      () => [
      {
        key: '__icon',
        title: <Text textAlign="center"> </Text>, // Empty title for the first icon column
        dataKey: '',
        width: 50,
        frozen: 'left',
        showOnMobile: true,
        cellRenderer: ({ cellData, rowData }) => {
          return (
            
            <Avatar
                size="sm"
                src={getUserProfilePic(rowData)}
                name={getUserFullName(rowData)}
              />
            );
        },
      },
      {
        key: 'name',
        title: <Text
        marginLeft="25px"
        fontSize="medium"
        fontWeight="600"
        textAlign="left"
        width="100%"
      >
        {t('myDisk.name') || "Name"}
      </Text>, // Left-aligned header
        dataKey: 'name',
        width: isMobile? "70%" : "25%",
        resizable: true,
        sortable: false,
        showOnMobile: true,
        cellRenderer: ({ cellData, rowData }) => {
          // Logging rowData and cellData here
      
          return (
            <Box maxWidth="70%">
              <Text  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  fontSize="smaller"
                  textAlign="left"
                  width="100%">
              <Link
                  onClick={(e) => {
                    selectSharedDiskUser(rowData);
                  }}
                >
                  {getUserFullName(rowData)}
                </Link>
              </Text>
            </Box>
          );
        },
      },
      
      
      {
        key: 'updated_at',
        title: <Text marginLeft="30px"
        fontSize="medium"
        fontWeight="600"
        textAlign="left"
        width="100%">{t('myDisk.modified') || "Modified"}</Text>, // Center-aligned header
        dataKey: 'updated_at',
        resizable: true,
        sortable: false,
        frozen: 'right',
        width: '30%',
        cellRenderer: ({ cellData, rowData, column }) => {
          // Use the updated_at from rowData?.file if available, else use cellData
          const updatedAt = cellData || rowData?.file?.updated_at;
          
          return (
            <Text  overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            fontSize="smaller"
            textAlign="left"
            width="100%">
              {hovered?.id === rowData?.id && (rowData?.access_type === 2 || rowData?.owner?.id === userProfileInfo?.id) ? (
                <HStack justifyContent="center">
                  {copyingLink ? (
                    <Spinner size="sm" />
                  ) : (
                    <Text onClick={onCopyLinkClick(rowData)} as={Link}>
                      {t('post.copyLink') || "Copy Link"}
                    </Text>
                  )}
                  <Tooltip label="Share">
                    <IconButton
                      onClick={(e) => {
                        setSelectedFile?.(rowData);
                        shareDisc?.onOpen?.();
                      }}
                      size="sm"
                    >
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                </HStack>
              ) : (
                moment(cellData)?.format('MM/DD/YYYY hh:mm a')
              )}
            </Text>
          );
        }
      },      
      {
        key: '__menu',
        title: '', // Empty title for the last column
        dataKey: '',
        frozen: 'right',
        showOnMobile:true,
        cellRenderer: ({ cellData, rowData }) => {
          // console.log("rwdata", accessType); 
          return (
              menu ?? (
                <SharedDiskUserItemMenu >

                  <IconButton variant="ghost">
                    <ThreeDot width="20" height="20" />
                  </IconButton>
                </SharedDiskUserItemMenu>
              )
          );
        },
      }
    ],
     [files, checkedFiles, hovered, copyingLink] );

    // console.log('selectedFile', selectedFile?.owner?.id === userProfileInfo?.id)
    const tableParentRef = useRef();
    const tabelParentSize = useDimensions(tableParentRef, true)?.borderBox;
    const { width = 640, height = 480 } = tabelParentSize ?? {};

    const onCopyLinkClick = (file) => async (e) => {
      try {
        setCopyingLink(true);
        setSelectedFile?.(file);
        await refetchSharedFileLink?.();

        if (!sharedFileLink || sharedFileLink?.status_code > 400) {

          if (
            !sharedFileLink ||
            sharedFileLink?.detail?.includes?.('not have public share link')
          ) {
            try { await generateShareLink?.(file); } catch (e) {
              console.log(e);
            }

          }
        } else {
          copyTextToClipboard(sharedFileLink?.public_share_deep_link);
          toast({ title:  t('myDisk.linkCopiedSuccessfully') || 'Link copied successfully!', status: 'success', position: 'top', });
        }
      } catch (e) {
        toast({ title: e.message, status: 'error' });
      } finally {
        setCopyingLink(false);
      }
    };
    return (
      <>
        <HStack align='flext-start' position="relative" width="100%" height="100%">
          <Stack ref={tableParentRef} width={'100%'} overflowX={"hidden"}>
            {/* table view */}
            {/* <SelectableTable
              onSelectedRowsChange={(rows) => setSelectedFiles(rows)}
              emptyRenderer={() => (
                <DiskNoFilesIllustration loading={filesLoading} />
              )}
              rowKey="rowKey"
              selectable={true}
              width={width}
              height={height}
              columns={isMobile ? cols.filter(item => item.showOnMobile) : cols}
              data={files} 
                           rowClassName={({ rowData }) =>
                rowData?.id === selectedFile?.id ? `active` : ``
              }

              hovered={hovered}
              rowEventHandlers={{
                onMouseEnter: ({ rowData }) => {
                  setHovered(rowData);
                },
                onMouseLeave: ({ rowData }) => {
                  setHovered(null);
                },
                onClick: ({ rowData }) => setSelectedFile(rowData),
                onDoubleClick: ({ rowData }) => selectSharedDiskUser(rowData),

              }}
            /> */}
            <SelectableTable
              onSelectedRowsChange={(rows) => setSelectedFiles(rows)}
              emptyRenderer={() => (
                <DiskNoFilesIllustration loading={filesLoading} />
              )}
              rowKey="rowKey"
              selectable={true}
              width={width}
              height={height}
              columns={isMobile ? cols.filter(item => item.showOnMobile) : cols}
              data={files?.map?.((f, i) => ({ ...f, rowKey: f?.id + '_' + i }))}  // ensure unique rowKey
              rowClassName={({ rowData }) =>
                rowData?.id === selectedFile?.id ? `active` : ``
              }
              hovered={hovered}
              rowEventHandlers={{
                onMouseEnter: ({ rowData }) => setHovered(rowData),
                onMouseLeave: () => setHovered(null),
                onClick: ({ rowData }) => setSelectedFile(rowData),
                onDoubleClick: ({ rowData }) => selectSharedDiskUser(rowData),
              }}
            />
          </Stack>
        </HStack>
      </>
    );
  }
);
